import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import {
  catchError,
  filter,
  first,
  map,
  startWith,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { getEnumAsArray } from 'src/app/shared/utils'
import { Competition } from '../../competition'
import {
  BlacklistWhitelistDays,
  CompetitionBlacklist
} from '../../competition-blacklist/competition-blacklist'
import { CompetitionMatch } from '../../competition-match/competition-match'
import { competitionMatchActions } from '../../competition-match/competition-match.actions'
import { competitionMatchSelectors } from '../../competition-match/competition-match.selectors'
import { CompetitionTeam } from '../../competition-team/competition-team'
import { competitionTeamSelectors } from '../../competition-team/competition-team.selectors'
import { competitionActions } from '../../competition.actions'
import { competitionSelectors } from '../../competition.selectors'
import { CompetitionWhitelist } from '../competition-whitelist'
import { competitionWhitelistActions } from '../competition-whitelist.actions'
import { competitionWhitelistSelectors } from '../competition-whitelist.selectors'
import { competitionBlacklistSelectors } from '../../competition-blacklist/competition-blacklist.selectors'
import { ActivatedRoute } from '@angular/router'

@UntilDestroy()
@Component({
  selector: 'whitelist-card',
  templateUrl: './whitelist-card.component.html',
  styleUrls: ['./whitelist-card.component.scss']
})
export class WhiteListCardComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<any>()
  @Output() onSelectWhiteListDay = new EventEmitter<any>()
  public form: UntypedFormGroup
  public days = getEnumAsArray(BlacklistWhitelistDays)
  public dayOptions$: Observable<any[]>
  public teamId$: Observable<number>
  public team$: Observable<CompetitionTeam>
  public isDisabled: boolean = false
  public manualDateInput: boolean = false
  public competitionEntities$: Observable<Dictionary<Competition>>
  public selectedDayIds: any[]
  public selectedWhiteListDays$: Observable<CompetitionWhitelist[]>
  public selectedBlackListDays$: Observable<CompetitionBlacklist[]>
  public selectedLanguage$: Observable<string>
  public competitionMatches$: Observable<CompetitionMatch[]>
  private teamId: number
  private whiteListData$ = new Subject<any>()
  private selectWhiteList$: Observable<any>
  private startDate: string
  private endDate: string
  private competitionId: number
  private selectedWhiteListDays: any[]

  get daysFormArray() {
    return this.form.controls.days as UntypedFormArray
  }

  constructor(
    private store: Store<AppState>,
    private fb: UntypedFormBuilder,
    private actions$: Actions,
    private notificationService: NotificationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.selectWhiteList$ = this.store.select(
      competitionWhitelistSelectors.selectCurrentWhiteListObjects
    )
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.competitionEntities$ = this.store.select(
      competitionSelectors.selectEntities
    )

    this.form = this.fb.group({
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      days: new UntypedFormArray([]),
      dateOption: ['wholeCompetition', Validators.required]
    })

    this.days.forEach(() => this.daysFormArray.push(new UntypedFormControl(false)))

    this.teamId$ = this.route.paramMap.pipe(
      map((params) => params.get('teamId')),
      map((id) => Number(id)),
      filter((id): id is number => !isNaN(id))
    )
    this.team$ = this.teamId$.pipe(
      switchMap((teamId) =>
        this.store.select(competitionTeamSelectors.selectById, teamId)
      ),
      tap((team) => {
        if (team && this.competitionId != team.competitionId) {
          this.competitionId = team?.competitionId
          this.store.dispatch(
            competitionActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { id: team?.competitionId }
              })
            })
          )
          this.store.dispatch(
            competitionMatchActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { competitionId: team?.competitionId },
                join: [{ field: 'booking' }]
              })
            })
          )
          this.store.dispatch(
            competitionWhitelistActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { competitionId: team?.competitionId }
              })
            })
          )
        }
      })
    )

    this.selectedBlackListDays$ = this.team$.pipe(
      switchMap((team) =>
        this.store.select(competitionBlacklistSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: team?.id
        })
      )
    )

    let data = []
    this.selectWhiteList$
      .pipe(take(1))
      .subscribe((whiteList) => (data = whiteList))

    this.selectedWhiteListDays$ = this.team$.pipe(
      switchMap((team) => {
        return this.store.select(competitionWhitelistSelectors.selectByFilter, {
          field: 'competitionTeamId',
          value: team?.id
        })
      }),

      map((selectedWhiteListDays) => {
        if (data.length == 0) {
          if (selectedWhiteListDays.length !== 0) {
            this.whiteListData$.next(selectedWhiteListDays)
          }
        }
        this.selectedWhiteListDays = selectedWhiteListDays.sort(
          (a, b) => a.weekday - b.weekday
        )
        return selectedWhiteListDays.sort((a, b) => a.weekday - b.weekday)
      })
    )

    this.competitionMatches$ = this.team$.pipe(
      switchMap((team) => {
        return this.store.select(competitionMatchSelectors.selectByFilter, {
          field: 'competitionId',
          value: team?.competitionId
        })
      })
    )

    this.dayOptions$ = combineLatest([
      this.store.select(bookingSelectors.selectEntities),
      this.selectedWhiteListDays$,
      this.store.select(
        competitionBlacklistSelectors.selectCurrentBlackListObjects
      )
    ]).pipe(
      untilDestroyed(this),
      map(
        ([bookingEntitites, selectedWhiteListDays, selectedBlackListDays]) => {
          let startTimesOfBookings = []

          for (let i = 0; i < 8; i++) {
            startTimesOfBookings[i] = []
          }

          let linkedProductId: number
          this.store
            .select(competitionSelectors.selectEntities)
            .pipe(take(1))
            .subscribe((competition) => {
              linkedProductId =
                competition[this.competitionId]?.linkedChildProductId
            })

          Object.values(bookingEntitites)
            .filter((b) => b.linkedProductId === linkedProductId)
            .forEach((booking) => {
              const date = moment(booking?.startDate)
              const dow = date.day() === 0 ? 7 : date.day()

              if (
                booking &&
                !selectedBlackListDays?.find(
                  (blackListDay) => blackListDay.weekday == dow
                ) &&
                !selectedWhiteListDays?.find(
                  (whiteListDay) => whiteListDay.weekday == dow
                )
              ) {
                startTimesOfBookings[dow].push(
                  moment(booking?.startDate).format('HH:mm')
                )
                startTimesOfBookings[dow].push(
                  moment(booking?.endDate).format('HH:mm')
                )
              }
            })

          Object.keys(startTimesOfBookings).forEach((day) => {
            startTimesOfBookings[day].sort(
              (a, b) => a.replace(':', '') - b.replace(':', '')
            )
          })

          return startTimesOfBookings
        }
      )
    )

    this.store.dispatch(
      competitionWhitelistActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { competitionTeamId: this.teamId }
        })
      })
    )

    this.whiteListData$.pipe(take(1)).subscribe((whiteList) => {
      this.store.dispatch(
        competitionWhitelistActions.setCurrentWhiteList({
          currentWhiteListSelected: whiteList
        })
      )
      if (whiteList.length === 0) {
        this.store.dispatch(
          competitionWhitelistActions.deleteAllCurrentWhiteList()
        )
      }
    })
    combineLatest([
      this.form.controls.dateOption.valueChanges.pipe(
        startWith('wholeCompetition')
      ),
      this.team$,
      this.competitionEntities$
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([startDate, team, competitionEntities]) => {
        if (startDate == 'wholeCompetition') {
          this.form.controls.startDate.patchValue(
            competitionEntities[team?.competitionId]?.startDate
          )
          this.form.controls.endDate.patchValue(
            competitionEntities[team?.competitionId]?.endDate
          )
        } else {
          this.form.controls.startDate.patchValue(null)
          this.form.controls.endDate.patchValue(null)
        }
      })

    this.form.controls.days.valueChanges
      .pipe(untilDestroyed(this), startWith([]))
      .subscribe((days) => {
        this.selectedDayIds = []

        days.forEach((checked, i) => {
          checked
            ? i == 0
              ? this.selectedDayIds.push(null)
              : this.selectedDayIds.push(i)
            : null
        })
      })
  }

  public onInputChange(value: string) {
    if (value == 'dateRange') {
      this.manualDateInput = true
    } else {
      this.manualDateInput = false
    }
  }

  public onClickRemove(whitelistDay) {
    this.store.dispatch(
      competitionWhitelistActions.deleteOne({ id: whitelistDay.id })
    )

    this.store.dispatch(
      competitionWhitelistActions.deleteCurrentWhiteList({
        currentWhiteListSelected: whitelistDay
      })
    )
    this.actions$
      .pipe(
        ofType(competitionWhitelistActions.removeOne),
        first(),
        timeout(5000),
        catchError((error) => {
          return of(null)
        })
      )
      .subscribe((action) => {
        if (!action) {
          this.notificationService.emit('alerts.timeoutError', 'warning')
          return
        }
        this.notificationService.emit('alerts.success', 'success')
      })
  }
  public dateChangeWhiteList(event: any, typeDate: string): void {
    if (event !== null) {
      if (typeDate === 'startDate') {
        this.startDate = moment(event._d).format('YYYY-MM-DD hh:mm:ss')
      } else {
        this.endDate = moment(event._d).format('YYYY-MM-DD hh:mm:ss')
      }
    }
  }
  public checkDates(): any {
    if (
      this.manualDateInput &&
      moment(this.endDate).diff(moment(this.startDate), 'days') <= 7 &&
      moment(this.endDate).diff(moment(this.startDate), 'days') > 0
    ) {
      return false
    } else {
      return true
    }
  }
  public onFormSubmit(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })

      console.log('%cForm is invalid', 'color: red; font-weight: bold;')
      return
    }

    const f = this.form.value

    this.team$.pipe(take(1)).subscribe((team) => {
      if (this.checkDates() && this.manualDateInput) {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].markAsDirty()
        })
        this.notificationService.emit('alerts.invalidDateRange', 'warning')
      } else {
        if (this.manualDateInput) {
          this.onSubmit.emit({
            ...f,
            days: [moment(f.startDate).isoWeekday()],
            startDate: moment(f.startDate),
            endDate: moment(f.endDate),
            teamId: team?.id
          })
        } else {
          this.onSubmit.emit({
            ...f,
            days: this.selectedDayIds,
            teamId: team?.id
          })

          this.daysFormArray.controls.forEach((day) => {
            day.setValue(false)
          })
        }
      }
    })

    this.daysFormArray.controls.forEach((day) => {
      day.setValue(false)
    })
  }

  public changeSelectedWhiteListOption(dayIndex, event) {
    this.team$.pipe(take(1)).subscribe((team) => {
      const whiteListSelected = {
        weekday: dayIndex,
        competitionId: team?.competitionId,
        competitionTeamId: team?.id,
        startDate: this.form.value.startDate,
        endDate: this.form.value.endDate,
        type: 'whitelist'
      }

      if (event.target.checked) {
        this.store.dispatch(
          competitionWhitelistActions.setCurrentWhiteList({
            currentWhiteListSelected: [whiteListSelected]
          })
        )
      } else {
        this.store.dispatch(
          competitionWhitelistActions.deleteCurrentWhiteList({
            currentWhiteListSelected: whiteListSelected
          })
        )
      }
    })
  }
}
